import { AlertTriangle } from 'lucide-react';
import type { TeamAllocationGridEntry } from '../../lib/dashboardApi';

interface TeamAllocationGridProps {
  entries: TeamAllocationGridEntry[];
}

const STATUS_META: Record<
  TeamAllocationGridEntry['status'],
  { label: string; badge: string; bar: string }
> = {
  free: { label: 'Liber', badge: 'bg-gray-200 text-gray-700', bar: 'bg-slate-400' },
  busy: { label: 'Ocupat', badge: 'bg-emerald-500/20 text-emerald-300', bar: 'bg-emerald-500' },
  overloaded: { label: 'Supraîncărcat', badge: 'bg-red-500/20 text-red-300', bar: 'bg-red-500' },
};

/**
 * Team allocation grid (CLE-256, Secțiunea 4). Per-person utilization bar,
 * active projects, in-progress task count and an over/under-load status flag.
 */
export function TeamAllocationGrid({ entries }: TeamAllocationGridProps) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <h3 className="text-sm font-semibold text-foreground">Alocare echipă</h3>

      {entries.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground/70">Nicio persoană activă.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {entries.map((entry) => {
            const meta = STATUS_META[entry.status];
            const util = Math.round(entry.utilizationPercent);
            const barWidth = Math.min(util, 100);
            return (
              <div
                key={entry.person.id}
                className="rounded-lg border border-border/60 bg-muted/20 p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {entry.person.name}
                      {entry.hasOverdueTasks && (
                        <AlertTriangle
                          className="ml-1 inline h-3.5 w-3.5 text-red-400"
                          aria-label="Are task-uri întârziate"
                        />
                      )}
                    </p>
                    {entry.person.title && (
                      <p className="truncate text-xs text-muted-foreground">{entry.person.title}</p>
                    )}
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.badge}`}
                  >
                    {meta.label}
                  </span>
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full ${meta.bar}`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <span className="w-12 shrink-0 text-right text-xs font-medium text-muted-foreground">
                    {util}%
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {entry.projects.slice(0, 4).map((p) => (
                    <span
                      key={p.projectId}
                      className="truncate rounded-md bg-muted/60 px-1.5 py-0.5 text-[11px] text-foreground"
                    >
                      {p.projectName}
                    </span>
                  ))}
                  {entry.projects.length > 4 && (
                    <span className="text-[11px] text-muted-foreground">
                      +{entry.projects.length - 4}
                    </span>
                  )}
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {entry.inProgressTasks} în lucru
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
